<template>
    <div class="form-group">
        <label class="form-label section-title">{{ $t('messages.placements') }}</label>
        <div class="bordered-container">
            <div class="segments-tabs">
                <button v-for="(group, index) in groups" :key="group.id"
                    :class="['tab-button', { active: activeGroupIndex === index }]" @click="switchGroup(index)">
                    {{ group.title }}
                    <span class="filter-count">{{ getSelectedCount(group) }}</span>
                </button>
            </div>

            <div class="controls-panel">
                <div class="search-input">
                    <img src="/images/search.svg" alt="search" class="search-icon" />
                    <input type="text" class="form-input" v-model="searchQuery"
                        :placeholder="$t('messages.search_segments_placeholder')" />
                    <button v-if="searchQuery" class="clear-search" @click="clearSearch">
                        <img src="/images/close.svg" alt="clear" />
                    </button>
                </div>
            </div>

            <div class="segments-content">
                <div class="segments-column">
                    <div class="segments-list">
                        <SegmentItem v-for="segment in filteredSegments" :key="segment.id" :segment="segment" :level="0"
                            :expanded-items="expandedItems" :selected="currentSelectedIds"
                            :should-show-description="true" @add-segment="addSegment" @remove-segment="removeSegment"
                            @toggle-expand="toggleExpand" />
                        <div v-if="filteredSegments.length === 0" class="empty-state">
                            {{ $t('messages.no_available_segments') }}
                        </div>
                    </div>
                </div>

                <div class="selected-column">
                    <div class="selected-header">
                        <div class="selected-label">{{ $t('messages.selected') }}</div>
                        <button class="btn-delete-all" @click="deleteAll">
                            {{ $t('messages.delete_all') }}
                        </button>
                    </div>
                    <div class="selected-content">
                        <div v-for="segment in currentSelectedSegments" :key="segment.id" class="selected-segment">
                            <span class="segment-name">{{ segment.title }}</span>
                            <button class="remove-btn" @click="removeSegment(segment)">
                                <img src="/images/circle-minus.svg" alt="remove" />
                            </button>
                        </div>
                        <div v-if="currentSelectedSegments.length === 0" class="empty-state">
                            {{ $t('messages.no_selected_segments') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SegmentItem from './SegmentItem.vue';

export default {
    name: "PlacementSegments",
    components: { SegmentItem },
    props: {
        placements: {
            type: Array,
            default: () => []
        },
        // flat array of selected placement ids or objects
        selected: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            searchQuery: '',
            expandedItems: new Set(),
            activeGroupIndex: 0,
            groups: [] // built from placements prop
        };
    },
    watch: {
        placements: {
            handler() { this.buildGroups(); },
            immediate: true,
            deep: true
        }
    },
    computed: {
        // flat numeric ids of selected placements
        currentSelectedIds() {
            const arr = Array.isArray(this.selected) ? this.selected : [];
            return arr.map(v => (v && typeof v === 'object' && 'id' in v) ? Number(v.id) : Number(v)).filter(n => Number.isFinite(n));
        },
        currentGroup() {
            return this.groups[this.activeGroupIndex] || null;
        },
        filteredSegments() {
            if (!this.currentGroup) return [];
            const list = this.currentGroup.children && this.currentGroup.children.length ? this.currentGroup.children : [this.currentGroup];
            if (!this.searchQuery) return list;
            return this.filterSegmentsRecursively(list, this.searchQuery.toLowerCase());
        },
        currentSelectedSegments() {
            const ids = this.currentSelectedIds.map(id => parseInt(id));
            const list = this.currentGroup ? (this.currentGroup.children && this.currentGroup.children.length ? this.currentGroup.children : [this.currentGroup]) : [];
            return ids.map(id => this.findSegmentById(list, id)).filter(s => s !== null);
        }
    },
    methods: {
        // Build hierarchical groups: top-level parents (no parent_id) become tabs
        buildGroups() {
            const items = Array.isArray(this.placements) ? this.placements.map(p => ({ ...p })) : [];
            const locale = (this.$i18n && this.$i18n.locale) ? this.$i18n.locale : 'ru';

            // prefer hierarchical formatting which also normalizes title/description
            const roots = this.formatHierarchicalSegments(items, locale);

            // Each root is a tab (group). If roots empty but items present, fall back to simple normalized list
            if (roots.length) {
                this.groups = roots;
            } else if (items.length) {
                this.groups = this.formatSimpleSegments(items, locale);
            } else {
                this.groups = [];
            }
        },

        formatSimpleSegments(items, locale) {
            return items.map(item => ({
                id: Number(item.id),
                title: item[`name_${locale}`] || item.name_ru || item.name_en || item.name_kk || item.title || '',
                description: item[`description_${locale}`] || item.description_ru || item.description_en || item.description_kk || item.description || '',
                parent_id: item.parent_id ? Number(item.parent_id) : null,
                children: item.children && Array.isArray(item.children) ? item.children : []
            }));
        },

        formatHierarchicalSegments(items, locale) {
            // Build map of normalized items then attach children by parent_id
            const itemMap = new Map();
            items.forEach(it => {
                const id = Number(it.id);
                itemMap.set(id, {
                    id,
                    parent_id: it.parent_id ? Number(it.parent_id) : null,
                    title: it[`name_${locale}`] || it.name_ru || it.name_en || it.name_kk || it.title || '',
                    description: it[`description_${locale}`] || it.description_ru || it.description_en || it.description_kk || it.description || '',
                    children: []
                });
            });

            const roots = [];
            itemMap.forEach(item => {
                const pid = item.parent_id !== null ? Number(item.parent_id) : null;
                if (pid !== null && itemMap.has(pid)) {
                    itemMap.get(pid).children.push(item);
                } else {
                    roots.push(item);
                }
            });
            return roots;
        },
        switchGroup(index) {
            this.activeGroupIndex = index;
            this.searchQuery = '';
            this.expandedItems.clear();
        },
        // Select a placement id and emit flat array of ids
        addSegment(segment) {
            const ids = new Set(this.currentSelectedIds || []);
            ids.add(Number(segment.id));
            this.emitSelection(Array.from(ids));
        },
        removeSegment(segment) {
            const ids = new Set(this.currentSelectedIds || []);
            ids.delete(Number(segment.id));
            this.emitSelection(Array.from(ids));
        },
        emitSelection(ids) {
            const normalized = Array.isArray(ids) ? Array.from(new Set(ids.map(Number).filter(Boolean))) : [];
            this.$emit('update:placements', normalized);
        },
        deleteAll() {
            const idsToRemove = [];
            const group = this.currentGroup;
            if (!group) {
                this.emitSelection(this.currentSelectedIds || []);
                return;
            }
            const collect = (items) => {
                for (const it of items) {
                    idsToRemove.push(Number(it.id));
                    if (it.children && it.children.length) collect(it.children);
                }
            };
            if (group.children && group.children.length) collect(group.children);
            else idsToRemove.push(Number(group.id));
            const remaining = (this.currentSelectedIds || []).filter(id => !idsToRemove.includes(Number(id)));
            this.emitSelection(Array.from(new Set(remaining)));
        },
        toggleExpand(item) {
            if (this.expandedItems.has(item.id)) this.expandedItems.delete(item.id);
            else this.expandedItems.add(item.id);
        },
        clearSearch() {
            this.searchQuery = '';
            this.expandedItems.clear();
        },
        filterSegmentsRecursively(segments, query) {
            const result = [];
            for (const segment of segments) {
                const matchesTitle = segment.title && segment.title.toLowerCase().includes(query);
                const matchesDescription = segment.description && segment.description.toLowerCase().includes(query);
                let matchingChildren = [];
                if (segment.children && segment.children.length > 0) {
                    matchingChildren = this.filterSegmentsRecursively(segment.children, query);
                }
                if (matchesTitle || matchesDescription || matchingChildren.length > 0) {
                    const filteredSegment = { ...segment };
                    if (matchingChildren.length > 0) {
                        filteredSegment.children = matchingChildren;
                        this.expandedItems.add(segment.id);
                    }
                    result.push(filteredSegment);
                }
            }
            return result;
        },
        getSelectedCount(group) {
            const ids = this.currentSelectedIds.map(id => parseInt(id));
            let count = 0;
            const walk = (items) => {
                for (const it of items) {
                    if (ids.includes(it.id)) count++;
                    if (it.children && it.children.length) walk(it.children);
                }
            };
            if (!group) return 0;
            if (group.children && group.children.length) walk(group.children);
            else if (ids.includes(group.id)) count++;
            return count;
        }
        ,

        findSegmentById(segments, id) {
            const numericId = parseInt(id);
            if (!Array.isArray(segments)) return null;
            for (const segment of segments) {
                if (segment.id === numericId || segment.id === id) {
                    return segment;
                }
                if (segment.children && segment.children.length > 0) {
                    const found = this.findSegmentById(segment.children, id);
                    if (found) return found;
                }
            }
            return null;
        }
    }
};
</script>

<style lang="scss" scoped>
/* reuse AudienceSegments styles where applicable */
.bordered-container {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    background: white;
    overflow: hidden;
}

.segments-tabs {
    display: flex;
    border-bottom: 1px solid #e9ecef;
    background: #f8f9fa;
}

.segments-content {
    display: flex;
    gap: 0;
    min-height: 320px;
}

.segments-column {
    flex: 0 0 70%;
    border-right: 1px solid #e9ecef;
}

.selected-column {
    flex: 0 0 30%;
    background: #f8f9fa;
    display: flex;
    flex-direction: column;
}

.selected-header {
    padding: 12px;
    border-bottom: 1px solid #e9ecef;
    background: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.selected-content {
    flex: 1;
    padding: 16px;
    overflow-y: auto;
}

.selected-segment {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 8px;
    font-size: 14px;
}

.segment-name {
    color: #495057;
    flex: 1;
    word-break: break-word;
}

.empty-state {
    color: #6c757d;
    font-style: italic;
    font-size: 14px;
    text-align: center;
    padding: 20px;
}

.tab-button {
    flex: 1;
    padding: 12px 16px;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.tab-button.active {
    color: #ec4899;
    border-bottom-color: #ec4899;
    background: white;
}

.filter-count {
    background: #6c757d;
    color: white;
    border-radius: 12px;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 600;
    min-width: 20px;
    text-align: center;
}

.tab-button.active .filter-count {
    background: #ec4899;
}
</style>
